import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LopSearchableSelect } from '../../components/LopSearchableSelect';
import {
  dismissSwapUfImportRow,
  getSwapUfAccounts,
  getSwapUfImportRows,
  resolveSwapUfImportRow,
  swapUfErrorMessage,
} from '../../api/swapUf';
import type { SwapUfAccount, SwapUfImportRow } from '../../types/SwapUf';
import { LopButton } from '../../components/LopButton';
import { t } from '../../i18n/t';
import { SwapUfModal } from './SwapUfModal';
import { formatDateOnly } from '../../utils/dateTime';

interface RowProps {
  row: SwapUfImportRow;
  notice: string | undefined;
  canEdit: boolean;
  busy: boolean;
  loadAccounts: (fundId: number) => Promise<SwapUfAccount[]>;
  onResolve: (row: SwapUfImportRow, acc: string) => Promise<void> | void;
  onDismiss: (row: SwapUfImportRow) => Promise<void> | void;
}

function ExceptionRow({ row, notice, canEdit, busy, loadAccounts, onResolve, onDismiss }: RowProps) {
  const [accounts, setAccounts] = useState<SwapUfAccount[]>([]);
  const [acc, setAcc] = useState<string | null>(null);

  useEffect(() => {
    if (row.fundId === null) return;
    let cancelled = false;
    loadAccounts(row.fundId)
      .then((a) => !cancelled && setAccounts(a))
      .catch(() => !cancelled && setAccounts([]));
    return () => {
      cancelled = true;
    };
  }, [row.fundId, loadAccounts]);

  const options = useMemo(
    () => accounts.map((a) => ({ id: a.acc, name: a.accName ?? a.fundName ?? '' })),
    [accounts],
  );

  return (
    <tr data-testid="swap-uf-exception-row">
      <td>{formatDateOnly(row.executionDate)}</td>
      <td>{row.fundNumber}</td>
      <td>{row.fundName}</td>
      <td>{row.accountRaw}</td>
      <td>{row.ticker}</td>
      <td>{row.side === 'Buy' || row.side === 'Sell' ? t(`swapUf.side.${row.side}`) : row.side}</td>
      <td>{row.qty === null ? '' : row.qty.toLocaleString('he-IL')}</td>
      <td>
        {row.exceptionReason}
        {notice && notice !== row.exceptionReason && (
          <div className="swap-uf-error" role="alert">
            {notice}
          </div>
        )}
      </td>
      {canEdit && (
        <td style={{ minWidth: 330 }}>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 150 }} title={row.fundId === null ? t('swapUf.exceptions.noFund') : undefined}>
              <LopSearchableSelect
                value={acc}
                options={options}
                onChange={setAcc}
                disabled={row.fundId === null || busy}
                placeholder={t('swapUf.exceptions.pickAccount')}
              />
            </div>
            <LopButton
              variant="primary"
              size="sm"
              disabled={acc === null || busy}
              onClick={() => (acc !== null ? onResolve(row, acc) : undefined)}
            >
              {t('swapUf.exceptions.assign')}
            </LopButton>
            <LopButton variant="secondary" size="sm" disabled={busy} onClick={() => onDismiss(row)}>
              {t('swapUf.exceptions.dismiss')}
            </LopButton>
          </div>
        </td>
      )}
    </tr>
  );
}

interface Props {
  canEdit: boolean;
  onClose: () => void;
  onChanged: () => void;
}

export function SwapUfExceptionsModal({ canEdit, onClose, onChanged }: Props) {
  const [rows, setRows] = useState<SwapUfImportRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notices, setNotices] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const cache = useRef(new Map<number, Promise<SwapUfAccount[]>>());

  const loadAccounts = useCallback((fundId: number) => {
    let p = cache.current.get(fundId);
    if (!p) {
      p = getSwapUfAccounts(fundId);
      cache.current.set(fundId, p);
    }
    return p;
  }, []);

  const load = useCallback(async () => {
    try {
      setRows(await getSwapUfImportRows({ status: 'exception' }));
    } catch (e) {
      setError(swapUfErrorMessage(e));
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const setNotice = (id: number, msg: string | null) =>
    setNotices((n) => {
      const next = { ...n };
      if (msg) next[id] = msg;
      else delete next[id];
      return next;
    });

  const resolve = useCallback(
    async (row: SwapUfImportRow, acc: string) => {
      setBusy(true);
      setError(null);
      setNotice(row.importRowId, null);
      try {
        const r = await resolveSwapUfImportRow(row.importRowId, acc);
        // A refused resolve may come back as 200 with the row still an exception and a new reason.
        if (r && r.status === 'exception' && r.exceptionReason) setNotice(row.importRowId, r.exceptionReason);
        await load();
        onChanged();
      } catch (e) {
        setNotice(row.importRowId, swapUfErrorMessage(e));
      } finally {
        setBusy(false);
      }
    },
    [load, onChanged],
  );

  const dismiss = useCallback(
    async (row: SwapUfImportRow) => {
      if (!window.confirm(t('swapUf.exceptions.dismissConfirm'))) return;
      setBusy(true);
      setError(null);
      try {
        await dismissSwapUfImportRow(row.importRowId);
        await load();
        onChanged();
      } catch (e) {
        setNotice(row.importRowId, swapUfErrorMessage(e));
      } finally {
        setBusy(false);
      }
    },
    [load, onChanged],
  );

  return (
    <SwapUfModal title={t('swapUf.exceptions.title')} onClose={onClose} wide>
      {error && (
        <div className="swap-uf-error" role="alert">
          {error}
        </div>
      )}
      {rows !== null && rows.length === 0 && !error && (
        <p className="swap-uf-info" id="swap-uf-exceptions-empty">
          {t('swapUf.exceptions.empty')}
        </p>
      )}
      {rows !== null && rows.length > 0 && (
        <div className="swap-uf-table-wrap">
          <table className="swap-uf-table" id="swap-uf-exceptions-table">
            <thead>
              <tr>
                <th>{t('swapUf.exceptions.colDate')}</th>
                <th>{t('swapUf.import.colFundNumber')}</th>
                <th>{t('swapUf.import.colFundName')}</th>
                <th>{t('swapUf.exceptions.colAccountRaw')}</th>
                <th>{t('swapUf.import.colTicker')}</th>
                <th>{t('swapUf.import.colSide')}</th>
                <th>{t('swapUf.import.colQty')}</th>
                <th>{t('swapUf.import.colReason')}</th>
                {canEdit && <th>{t('swapUf.exceptions.colActions')}</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <ExceptionRow
                  key={r.importRowId}
                  row={r}
                  notice={notices[r.importRowId]}
                  canEdit={canEdit}
                  busy={busy}
                  loadAccounts={loadAccounts}
                  onResolve={resolve}
                  onDismiss={dismiss}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SwapUfModal>
  );
}
